import React, { useState, useEffect, useMemo } from 'react';
import { getDailyUnits, getLanthandel, veckobrev } from '../qlik';

// ── VECKO-/MÅNADSBREV (Ledning) ───────────────────────────────────
// AI-utkast till brev för personalen: periodens intäkter per avdelning
// mot samma period ifjol + valfri kontext från ledningen. Utkastet visas
// för redigering/kopiering — inget skickas automatiskt någonstans.
// Samma komponent driver båda flikarna via prop `typ` ('vecka' | 'manad').

const LEDNING_COLOR = '#1F3A5F';

// ISO-veckonyckel (yyyyww) för ett ISO-datum
function veckoKeyAv(iso) {
  const t = new Date(iso + 'T12:00:00Z');
  const dow = (t.getUTCDay() + 6) % 7;
  t.setUTCDate(t.getUTCDate() - dow + 3);
  const y = t.getUTCFullYear();
  const jan4 = new Date(Date.UTC(y, 0, 4));
  const w = 1 + Math.round(((t - jan4) / 86400000 - 3 + ((jan4.getUTCDay() + 6) % 7)) / 7);
  return y * 100 + w;
}
// Månadsnyckel (yyyymm) för ett ISO-datum
const manadKeyAv = (iso) => Number(iso.slice(0, 4)) * 100 + Number(iso.slice(5, 7));
const veckoLabelAv = (key) => `v.${key % 100} ${Math.floor(key / 100)}`;
const MANADER = ['jan', 'feb', 'mars', 'april', 'maj', 'juni', 'juli', 'aug', 'sep', 'okt', 'nov', 'dec'];
const manadLabelAv = (key) => `${MANADER[(key % 100) - 1]} ${Math.floor(key / 100)}`;
const tkr = (v) => `${Math.round(v / 1000).toLocaleString('sv-SE')} tkr`;

export default function VeckobrevTab() {
  const [typ, setTyp] = useState('vecka'); // 'vecka' | 'manad' — samsas i samma flik
  const arManad = typ === 'manad';
  const keyAv = arManad ? manadKeyAv : veckoKeyAv;
  const labelAv = arManad ? manadLabelAv : veckoLabelAv;
  const [perVecka, setPerVecka] = useState(null); // veckoKey → { enhet: {intakt, lon} }
  const [lhVecka, setLhVecka] = useState({});     // veckoKey → oms (Lanthandeln)
  const [vecka, setVecka] = useState(null);
  const [anteckning, setAnteckning] = useState('');
  const [utkast, setUtkast] = useState('');
  const [jobb, setJobb] = useState(false);
  const [fel, setFel] = useState('');
  const [kopierat, setKopierat] = useState(false);
  const [avmarkerade, setAvmarkerade] = useState(() => new Set()); // avdelningar utanför summering/brev

  useEffect(() => {
    let live = true;
    setPerVecka(null); setVecka(null); setUtkast(''); setFel(''); // byte vecka↔månad
    (async () => {
      const [units, lh] = await Promise.all([getDailyUnits(), getLanthandel().catch(() => null)]);
      const pv = {};
      for (const u of units) {
        const k = keyAv(u.datum);
        const e = (pv[k] = pv[k] || {});
        const m = (e[u.enhet] = e[u.enhet] || { intakt: 0, lon: 0 });
        m.intakt += u.intakt || 0;
        m.lon += Math.abs(u.lon || 0);
      }
      const lhv = {};
      for (const [datum, oms] of (lh?.days || [])) {
        const k = keyAv(datum);
        lhv[k] = (lhv[k] || 0) + oms;
      }
      if (!live) return;
      setPerVecka(pv);
      setLhVecka(lhv);
      // Default: senaste HELA perioden (innevarande vecka/månad är ofullständig)
      const nuKey = keyAv(new Date().toISOString().slice(0, 10));
      const keys = Object.keys(pv).map(Number).filter((k) => k < nuKey).sort((a, b) => b - a);
      if (keys.length) setVecka(keys[0]);
    })().catch((e) => { console.error('brev data:', e); setFel('Kunde inte hämta periodens data'); });
    return () => { live = false; };
  }, [typ]); // eslint-disable-line react-hooks/exhaustive-deps

  const veckoVal = useMemo(() => {
    if (!perVecka) return [];
    const nuKey = keyAv(new Date().toISOString().slice(0, 10));
    return Object.keys(perVecka).map(Number).filter((k) => k <= nuKey).sort((a, b) => b - a).slice(0, arManad ? 12 : 8);
  }, [perVecka]); // eslint-disable-line react-hooks/exhaustive-deps

  // Veckans rader: per enhet + Lanthandeln, mot samma vecka ifjol (key − 100)
  const rader = useMemo(() => {
    if (!perVecka || !vecka) return [];
    const nu = perVecka[vecka] || {};
    const fj = perVecka[vecka - 100] || {};
    const enheter = new Set([...Object.keys(nu), ...Object.keys(fj)]);
    const ut = [];
    for (const enhet of enheter) {
      if (enhet === 'OH') continue; // internposter hör inte hemma i personalbrevet
      const n = nu[enhet] || { intakt: 0, lon: 0 };
      const f = fj[enhet] || { intakt: 0 };
      if (n.intakt < 1000 && f.intakt < 1000) continue;
      ut.push({
        namn: enhet,
        intakt: Math.round(n.intakt),
        intaktIfjol: Math.round(f.intakt),
        lon: Math.round(n.lon),
      });
    }
    const lhNu = lhVecka[vecka] || 0;
    const lhFj = lhVecka[vecka - 100] || 0;
    if (lhNu > 1000 || lhFj > 1000) {
      ut.push({ namn: 'Stigmyhrs Lanthandel', intakt: Math.round(lhNu), intaktIfjol: Math.round(lhFj) });
    }
    return ut.sort((a, b) => b.intakt - a.intakt);
  }, [perVecka, lhVecka, vecka]);

  // Valda avdelningar styr BÅDE summeringen och vad brevet skrivs om.
  // Avmarkeringar sparas på namn → följer med när du byter period.
  const valda = useMemo(() => rader.filter((r) => !avmarkerade.has(r.namn)), [rader, avmarkerade]);
  const toggleEnhet = (namn) => {
    setAvmarkerade((prev) => {
      const n = new Set(prev);
      if (n.has(namn)) n.delete(namn); else n.add(namn);
      return n;
    });
    setUtkast('');
  };

  // Summering. Δ% räknas bara på enheter som HAR fjolårsdata — annars skulle
  // t.ex. Lanthandeln (ingen historik) blåsa upp jämförelsen.
  const total = useMemo(() => {
    const t = { intakt: 0, ifjol: 0, lon: 0, jmfNu: 0, jmfIfjol: 0, utanFjol: [] };
    for (const r of valda) {
      t.intakt += r.intakt; t.ifjol += r.intaktIfjol || 0; t.lon += r.lon || 0;
      if (r.intaktIfjol > 0) { t.jmfNu += r.intakt; t.jmfIfjol += r.intaktIfjol; }
      else t.utanFjol.push(r.namn);
    }
    t.diff = t.jmfIfjol > 0 ? (t.jmfNu / t.jmfIfjol - 1) * 100 : null;
    return t;
  }, [valda]);

  const generera = async () => {
    if (!valda.length || jobb) return;
    setJobb(true); setFel(''); setKopierat(false);
    try {
      const res = await veckobrev({
        veckoLabel: labelAv(vecka), vecka, rader: valda, anteckning, typ,
        total: { intakt: Math.round(total.intakt), ifjol: Math.round(total.ifjol),
                 jmfNu: Math.round(total.jmfNu), jmfIfjol: Math.round(total.jmfIfjol),
                 utanFjol: total.utanFjol },
      });
      setUtkast(res.utkast || '');
    } catch (e) {
      setFel(e.message || 'Kunde inte skriva utkastet');
    } finally { setJobb(false); }
  };

  const kopiera = async () => {
    try {
      await navigator.clipboard.writeText(utkast);
      setKopierat(true);
      setTimeout(() => setKopierat(false), 2500);
    } catch { setFel('Kunde inte kopiera — markera texten manuellt'); }
  };

  const chip = (active) => ({
    background: active ? LEDNING_COLOR : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
    border: '1px solid var(--border)', borderRadius: 20, padding: '4px 12px',
    fontSize: 11.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif',
  });
  const td = { fontSize: 11.5, padding: '3px 8px 3px 0', fontFamily: 'system-ui,sans-serif', textAlign: 'right', whiteSpace: 'nowrap' };

  return (
    <>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>📝 {arManad ? 'Månadsbrev' : 'Veckobrev'} till personalen — AI-utkast</div>
          <div style={{ display: 'flex', gap: 5 }}>
            <button onClick={() => setTyp('vecka')} style={chip(!arManad)}>Veckobrev</button>
            <button onClick={() => setTyp('manad')} style={chip(arManad)}>Månadsbrev</button>
          </div>
        </div>
        {!perVecka && !fel && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Hämtar {arManad ? 'månadsdata' : 'veckodata'}…</div>}
        {perVecka && (
          <>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              {veckoVal.map((k) => (
                <button key={k} onClick={() => { setVecka(k); setUtkast(''); }} style={chip(vecka === k)}>{labelAv(k)}</button>
              ))}
            </div>

            {rader.length > 0 && (
              <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 10 }}>
                <thead>
                  <tr>
                    <th style={{ ...td, width: 26, textAlign: 'center', padding: '3px 4px 3px 0' }}>
                      <input type="checkbox" title="Markera/avmarkera alla"
                        checked={valda.length === rader.length}
                        ref={(el) => { if (el) el.indeterminate = valda.length > 0 && valda.length < rader.length; }}
                        onChange={() => setAvmarkerade(valda.length === rader.length ? new Set(rader.map((r) => r.namn)) : new Set())}
                        style={{ cursor: 'pointer' }} />
                    </th>
                    {['Avdelning', 'Intäkt', 'Ifjol', 'Δ', 'Personal%'].map((h, i) => (
                      <th key={i} style={{ ...td, textAlign: i === 0 ? 'left' : 'right', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rader.map((r) => {
                    const diff = r.intaktIfjol > 0 ? (r.intakt / r.intaktIfjol - 1) * 100 : null;
                    const med = !avmarkerade.has(r.namn);
                    return (
                      <tr key={r.namn} style={{ borderTop: '1px solid var(--border)', opacity: med ? 1 : 0.4 }}>
                        <td style={{ ...td, textAlign: 'center', padding: '3px 4px 3px 0' }}>
                          <input type="checkbox" checked={med} onChange={() => toggleEnhet(r.namn)} style={{ cursor: 'pointer' }} />
                        </td>
                        <td style={{ ...td, textAlign: 'left', fontWeight: 600, cursor: 'pointer', textDecoration: med ? 'none' : 'line-through' }}
                          onClick={() => toggleEnhet(r.namn)}>{r.namn}</td>
                        <td style={td}>{tkr(r.intakt)}</td>
                        <td style={{ ...td, color: 'var(--text-secondary)' }}>{r.intaktIfjol ? tkr(r.intaktIfjol) : '–'}</td>
                        <td style={{ ...td, fontWeight: 600, color: diff === null ? 'var(--text-secondary)' : diff >= 0 ? '#2E7D32' : '#A32D2D' }}>
                          {diff === null ? '–' : `${diff >= 0 ? '+' : ''}${diff.toFixed(0)} %`}
                        </td>
                        <td style={{ ...td, color: 'var(--text-secondary)' }}>{r.lon && r.intakt ? `${Math.round((r.lon / r.intakt) * 100)} %` : '–'}</td>
                      </tr>
                    );
                  })}
                  <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700 }}>
                    <td style={{ ...td, padding: '3px 4px 3px 0' }} />
                    <td style={{ ...td, textAlign: 'left' }}>TOTALT{valda.length < rader.length ? ` (${valda.length} av ${rader.length})` : ''}</td>
                    <td style={td}>{tkr(total.intakt)}</td>
                    <td style={{ ...td, color: 'var(--text-secondary)' }}>{total.ifjol ? tkr(total.ifjol) : '–'}</td>
                    <td style={{ ...td, color: total.diff === null ? 'var(--text-secondary)' : total.diff >= 0 ? '#2E7D32' : '#A32D2D' }}>
                      {total.diff === null ? '–' : `${total.diff >= 0 ? '+' : ''}${total.diff.toFixed(0)} %`}
                    </td>
                    <td style={{ ...td, color: 'var(--text-secondary)' }}>{total.lon && total.intakt ? `${Math.round((total.lon / total.intakt) * 100)} %` : '–'}</td>
                  </tr>
                </tbody>
              </table>
            )}
            {valda.length > 0 && total.utanFjol.length > 0 && (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', marginTop: -4, marginBottom: 10 }}>
                Δ jämför enheter med fjolårsdata ({tkr(total.jmfNu)} mot {tkr(total.jmfIfjol)}). Utan jämförelse: {total.utanFjol.join(', ')}.
              </div>
            )}

            <textarea value={anteckning} onChange={(e) => setAnteckning(e.target.value)}
              placeholder={arManad
                ? 'Kontext till brevet (valfritt), t.ex. "vi drog igång nya frukostkonceptet i mitten av månaden" eller "nämn att vintersäsongen drar igång i november"…'
                : 'Kontext till brevet (valfritt), t.ex. "Bruksvallsloppet i helgen — tacka alla som ställde upp extra" eller "påminn om personalfesten 8/8"…'}
              style={{ width: '100%', minHeight: 54, border: '1px solid var(--border)', borderRadius: 8, padding: 8, fontSize: 12.5, fontFamily: 'system-ui,sans-serif', background: 'var(--card)', color: 'var(--text)', resize: 'vertical', marginBottom: 8 }} />

            <button onClick={generera} disabled={jobb || !valda.length}
              style={{ background: LEDNING_COLOR, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif', opacity: jobb ? 0.6 : 1 }}>
              {jobb ? '✍️ Skriver…' : utkast ? '↻ Generera nytt utkast' : '✍️ Generera utkast'}
            </button>
          </>
        )}
        {fel && <div style={{ fontSize: 12, color: '#A32D2D', fontFamily: 'system-ui,sans-serif', marginTop: 8 }}>{fel}</div>}
      </div>

      {utkast && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            <div className="card-title" style={{ marginBottom: 0 }}>Utkast — {labelAv(vecka)}{arManad ? ' (månadsbrev)' : ''}</div>
            <button onClick={kopiera} style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'system-ui,sans-serif', color: 'var(--text)' }}>
              {kopierat ? '✓ Kopierat' : '⧉ Kopiera'}
            </button>
          </div>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 13.5, lineHeight: 1.6, fontFamily: 'system-ui,sans-serif' }}>{utkast}</div>
          <div className="note">Utkast — läs igenom, justera och skicka själv (mail/anslagstavla). Inget skickas automatiskt.</div>
        </div>
      )}
    </>
  );
}
